import { useMutation } from '@apollo/client'
import { UntitledIcon } from '@faceup/icons'
import { ulMinusCircle } from '@faceup/icons/ulMinusCircle'
import { ulPlus } from '@faceup/icons/ulPlus'
import { Link } from '@faceup/router'
import { Form } from '@faceup/ui'
import { Button, Input, useMessage, useNotification } from '@faceup/ui-base'
import { Country, isEmail, Plan } from '@faceup/utils'
import { type ComponentProps, type MouseEvent, memo, useState } from 'react'
import { graphql } from '../__generated__'

const mutations = {
  SendReportEmailsMutation: graphql(`
    mutation SendReportEmailsMutation($input: SendReportEmailsInput!) {
      sendReportEmails(input: $input) {
        editedCase {
          id
          ... on CompanyReport {
            emailsSent
          }
          organizationalUnit {
            id
            organizationalUnitName
            country
            billing {
              id
              plan
            }

            isVerified
            isPendingVerification
          }
        }
      }
    }
  `),
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

type Props = {
  id: string
  closed: boolean | null
  emailsSent: boolean | null
  organizationalUnit: {
    id?: string | null
    name?: string | null
    isVerified: boolean | null
    isPendingVerification: boolean | null
    country: Country | null
    billing: {
      plan: Plan | null
    } | null
  }
}

const NonRegisteredEmails = memo(({ organizationalUnit, closed, emailsSent, id }: Props) => {
  const [emails, setEmails] = useState<string[]>([''])
  const message = useMessage()
  const notification = useNotification()

  const [sendReportEmails, { loading }] = useMutation(mutations.SendReportEmailsMutation, {
    onCompleted: () => message.success('E-mails sent'),
  })

  // eg do not wait for registration
  const showForm =
    organizationalUnit?.country === Country.Cz &&
    organizationalUnit?.billing?.plan === Plan.Unregistered &&
    !closed

  const validateForm = () => {
    const wrongEmails = emails.filter(email => !isEmail(email))
    if (wrongEmails.length) {
      notification.error({
        message: 'Following emails are invalid',
        description: wrongEmails.join(', '),
      })
      return false
    }

    return true
  }

  if (organizationalUnit?.isPendingVerification) {
    return (
      <>
        Institution{' '}
        <Link to={routes => routes.institution({ id: organizationalUnit.id ?? '' })}>
          {organizationalUnit?.name}
        </Link>{' '}
        is pending verification. Verify it please first
      </>
    )
  }

  if (!showForm) {
    return null
  }

  return (
    <>
      <div className='flex w-full'>
        <div className='w-[10%]'>
          <h4 className='mt-2.5'>E-mail</h4>
        </div>

        <EmailsContent>
          {emails.map((email, key) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Email list with dynamic add/remove, index is stable identifier
            <Form.Item key={key} {...formItemLayout} withAsterisk>
              <Input
                className='me-2 w-[90%]'
                type='email'
                autoComplete='email'
                placeholder='Add e-mail'
                value={email}
                onChange={({ target: { value } }) =>
                  setEmails(emails.map((e, idx) => (idx === key ? value : e)))
                }
              />
              <UntitledIcon
                icon={ulMinusCircle}
                onClick={() => setEmails(emails.filter((_, idx) => idx !== key))}
              />
            </Form.Item>
          ))}

          <EmailsContent>
            <Button block onClick={() => setEmails([...emails, ''])}>
              <UntitledIcon icon={ulPlus} />
              Add e-mail
            </Button>
          </EmailsContent>
        </EmailsContent>
      </div>
      <div className='mt-5 flex flex-row justify-end'>
        <Button
          loading={loading}
          disabled={emails?.length === 0}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()

            if (validateForm()) {
              setEmails([])
              sendReportEmails({
                variables: {
                  input: {
                    caseId: id,
                    emailsForUnregisteredInstitution: emails,
                  },
                },
              })
            }
          }}
        >
          Send e-mails
          {emailsSent ? (
            <>
              &nbsp;AGAIN <i>(e-mails already sent)</i>
            </>
          ) : null}
        </Button>
      </div>
    </>
  )
})

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const EmailsContent = (props: ComponentProps<'div'>) => <div className='w-[90%]' {...props} />

NonRegisteredEmails.displayName = NonRegisteredEmails.name

export default NonRegisteredEmails
